import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Scene } from '@/types/scriptAnalysis';
import { getConfidenceLevel } from '@/types/scriptAnalysis';
import { formatEighths } from '@/lib/eighths';
import { sceneHeading } from '@/lib/scene-heading';

interface SceneTableProps {
  scenes: Scene[];
}

const confidenceConfig = {
  high: {
    label: 'Alta',
    className: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400 border-transparent',
  },
  medium: {
    label: 'Media',
    className: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400 border-transparent',
  },
  low: {
    label: 'Baja',
    className: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400 border-transparent',
  },
};

const statusConfig = {
  draft: { label: 'Borrador', variant: 'outline' as const },
  confirmed: { label: 'Confirmado', variant: 'default' as const },
  rejected: { label: 'Rechazado', variant: 'destructive' as const },
};

const intExtConfig = {
  INT: { variant: 'secondary' as const },
  EXT: { variant: 'outline' as const },
  'INT/EXT': { variant: 'default' as const },
};

export function SceneTable({ scenes }: SceneTableProps) {
  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">#</TableHead>
            <TableHead scope="col">Encabezado</TableHead>
            <TableHead scope="col">INT/EXT</TableHead>
            <TableHead scope="col">Localizacion</TableHead>
            <TableHead scope="col">Momento</TableHead>
            <TableHead scope="col">Octavos</TableHead>
            <TableHead scope="col">Confianza</TableHead>
            <TableHead scope="col">Estado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {scenes.map((scene) => {
            const level = getConfidenceLevel(scene.confidence);
            const conf = confidenceConfig[level];
            const status = statusConfig[scene.status];
            const intExt = intExtConfig[scene.int_ext];

            return (
              <TableRow key={scene.id}>
                <TableCell>{scene.scene_number}</TableCell>
                <TableCell className="font-medium">{sceneHeading(scene)}</TableCell>
                <TableCell>
                  <Badge variant={intExt.variant}>{scene.int_ext}</Badge>
                </TableCell>
                <TableCell>{scene.location_name}</TableCell>
                <TableCell>{scene.time_of_day}</TableCell>
                <TableCell>{formatEighths(scene.page_eighths)}</TableCell>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge className={conf.className}>
                        {conf.label}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Confianza de la IA: {(scene.confidence * 100).toFixed(0)}% - Los elementos marcados como borrador requieren revision manual.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Badge variant={status.variant}>{status.label}</Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
}
